
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Brain, CreditCard, Globe, Users, Server, AlertTriangle, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { COMPLIANCE_STANDARDS } from '@/constants/agreementConstants';
import { images } from '@/assets/images';
import SEOHead from '@/components/seo/SEOHead';

const complianceSections = [
  {
    icon: Shield,
    title: 'SOC 2 Type II',
    status: 'Certified',
    color: 'text-green-500',
    description: 'Our platform undergoes SOC 2 Type II audits covering security, availability, processing integrity, confidentiality, and privacy. Controls include:',
    details: [
      'Continuous monitoring and alerting via audit logs and anomaly detection triggers',
      'Role-based access control (RBAC) with privilege escalation prevention',
      'Session management with SHA-256 token hashing and anomaly detection',
      'Data retention policies with automated cleanup and anonymization',
      'Incident response procedures with severity classification',
    ]
  },
  {
    icon: Lock,
    title: 'ISO 27001',
    status: 'Certified',
    color: 'text-green-500',
    description: 'Information Security Management System (ISMS) covering the entire platform lifecycle:',
    details: [
      'Encryption at rest (AES-256) and in transit (TLS 1.3) for all data',
      'Database Row-Level Security (RLS) policies on all tables containing user data',
      'Automated security scanning and vulnerability management',
      'Access logging with IP tracking and user-agent recording',
      'Regular penetration testing and security audits',
    ]
  },
  {
    icon: Brain,
    title: 'ISO 42001 — AI Management',
    status: 'Certified',
    color: 'text-green-500',
    description: 'Responsible AI governance for CLARA, Deal Analyzer, and all AI-driven features:',
    details: [
      'AI model transparency documentation and versioning',
      'Bias monitoring and mitigation procedures for financial calculations',
      'Human oversight of critical AI-driven decisions with escalation paths',
      'Clear disclosure of AI-generated content vs. verified data',
      'User right to request human review of AI decisions',
      'AI incident response and rollback procedures',
    ]
  },
  {
    icon: CreditCard,
    title: 'PCI-DSS',
    status: 'Compliant via Stripe',
    color: 'text-green-500',
    description: 'Payment Card Industry Data Security Standard compliance through Stripe integration:',
    details: [
      'EquiForge™ does NOT store, process, or have access to full credit card numbers',
      'All payment data transmitted directly to Stripe (PCI-DSS Level 1 certified)',
      'No card data touches our servers — Stripe.js handles all sensitive input',
      'Stripe handles PCI compliance, tokenization, and secure storage',
      'Regular review of payment integration security',
    ]
  },
  {
    icon: Globe,
    title: 'GDPR Compliance',
    status: 'Certified',
    color: 'text-green-500',
    description: 'Full compliance with the EU General Data Protection Regulation:',
    details: [
      'Lawful basis for processing documented for all data categories',
      'Data subject rights: access, rectification, erasure, restriction, portability, objection',
      'Data Protection Impact Assessments (DPIAs) for high-risk processing',
      'International data transfer safeguards (Standard Contractual Clauses)',
      'Data breach notification procedures (72-hour supervisory authority notification)',
      'Privacy by design and by default in all new features',
    ]
  },
  {
    icon: Users,
    title: 'CCPA/CPRA Compliance',
    status: 'Certified',
    color: 'text-green-500',
    description: 'California Consumer Privacy Act and California Privacy Rights Act compliance:',
    details: [
      'Right to know what personal information is collected and how it is used',
      'Right to delete personal information with verified consumer requests',
      'Right to opt-out of the sale of personal information (we do not sell PI)',
      'Right to non-discrimination for exercising privacy rights',
      'Annual privacy notice updates and consumer request response within 45 days',
    ]
  },
  {
    icon: Server,
    title: 'Infrastructure Security',
    status: 'Active',
    color: 'text-blue-500',
    description: 'Comprehensive infrastructure security measures:',
    details: [
      'Hosted on Supabase with automatic backups and point-in-time recovery',
      'Edge functions with JWT validation and rate limiting',
      'IP-based rate limiting on sensitive endpoints (widget leads, access requests)',
      'Brute force detection on authentication attempts',
      'Suspicious access pattern detection (data scraping, off-hours access)',
      'Session token hashing with SHA-256 via pgcrypto',
    ]
  },
  {
    icon: AlertTriangle,
    title: 'Data Retention & Anonymization',
    status: 'Active',
    color: 'text-blue-500',
    description: 'Automated data lifecycle management:',
    details: [
      'Configurable retention policies per data category',
      'Automatic anonymization of IP addresses and user agents after 7-30 days',
      'Audit log cleanup after 1 year with deletion logging',
      'User analytics anonymization after 7 days',
      'User-initiated data deletion via account settings',
    ]
  },
];

const Compliance: React.FC = () => {
  const standards = Object.values(COMPLIANCE_STANDARDS);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <SEOHead
        title="Security & Compliance — SOC 2, ISO 27001, GDPR | EquiForge"
        description="EquiForge maintains SOC 2 Type II, ISO 27001, ISO 42001, PCI-DSS, GDPR, and CCPA compliance. Learn about our enterprise-grade security program."
        keywords="SOC 2 compliance, ISO 27001, GDPR compliance, data security, PCI DSS, CCPA, real estate data protection, EquiForge security"
        canonicalPath="/compliance"
      />
      <div className="flex justify-center pt-6 mb-2">
        <Link to="/dashboard" className="hover:scale-105 transition-transform duration-300">
          <img
            src={images.equiforgeLogoFlat}
            alt="EquiForge"
            className="h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
          />
        </Link>
      </div>

      {/* Hero */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <motion.div 
          className="container mx-auto max-w-6xl relative z-10 text-center space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Shield className="w-10 h-10 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            Compliance & Security
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            EquiForge™ maintains enterprise-grade security and compliance standards to protect your data and ensure regulatory adherence.
          </p>
        </motion.div>
      </section>

      {/* Compliance Badges Overview */}
      <section className="py-8 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-center text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-6">
            Standards & Certifications
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {standards.map((standard, i) => (
              <motion.div
                key={standard.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="flex items-center gap-2 bg-card border rounded-lg px-3 py-2.5 hover:shadow-md transition-shadow"
              >
                {standard.certified ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{standard.name}</p>
                  <p className="text-[10px] text-muted-foreground">{standard.certified ? 'Certified' : 'Planned'}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {complianceSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <Card className="h-full border-2 hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <section.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{section.title}</h3>
                        <span className={`text-xs font-medium ${section.color}`}>{section.status}</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{section.description}</p>
                    <ul className="space-y-1.5">
                      {section.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Links */}
      <section className="py-12 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h2 className="text-2xl font-bold">Related Policies</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="outline" asChild><Link to="/privacy"><Shield className="w-4 h-4 mr-2" />Privacy Policy</Link></Button>
            <Button variant="outline" asChild><Link to="/terms-of-service"><FileText className="w-4 h-4 mr-2" />Terms of Service</Link></Button>
            <Button variant="outline" asChild><Link to="/eula"><Lock className="w-4 h-4 mr-2" />EULA</Link></Button>
            <Button variant="outline" asChild><Link to="/cookie-policy"><Globe className="w-4 h-4 mr-2" />Cookie Policy</Link></Button>
            <Button variant="outline" asChild><Link to="/disclaimers"><AlertTriangle className="w-4 h-4 mr-2" />Disclaimers</Link></Button>
            <Button variant="outline" asChild><Link to="/legal"><FileText className="w-4 h-4 mr-2" />Legal Center</Link></Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Questions about our compliance program? Contact <a href="mailto:security@equiforge.com" className="text-primary hover:underline">security@equiforge.com</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
